import { Dispatch, FormEvent, SetStateAction, useContext, useState } from "react";

import { ProjectPhase } from "../../../../../../../../../../../constants/project-phase";
import { TemplateStepModalData } from "../../../../../../../../../../../models";
import { ServiceError, Status } from "../../../../../../../../../../../service/Shared";
import { updateWorkflowTemplateStep } from "../../../../../../../../../../../service/workflow/WorkflowService.full";
import { useIsLoadingWrapper } from "../../../../../../../../../../../utils";
import { Toast } from "../../../../../../../../../../../widget";
import { WorkflowTemplateContext } from "../../../../../../WorkflowTemplateContext";
import { WorkflowTemplateStepModel } from "../../../../models";

interface UseEditTemplateStepModalProps {
  closeModal: () => void;
  workflowTemplateStep: WorkflowTemplateStepModel;
}

interface UseEditTemplateStepModalReturnData {
  step: TemplateStepModalData;
  setStep: Dispatch<SetStateAction<TemplateStepModalData>>;

  handleEditTemplateStep: (e: FormEvent<HTMLFormElement>) => void;
  isHandleEditTemplateStepLoading: boolean;
  onCancel: () => void;
  errors: ServiceError[];
}

export const useEditTemplateStepModal = ({
  closeModal,
  workflowTemplateStep,
}: UseEditTemplateStepModalProps): UseEditTemplateStepModalReturnData => {
  const { shouldRefreshWorkflowTemplate } = useContext(WorkflowTemplateContext);
  const [isHandleEditTemplateStepLoading, setIsHandleEditTemplateStepLoading] = useState(false);
  const [errors, setErrors] = useState<ServiceError[]>([]);

  const [step, setStep] = useState<TemplateStepModalData>({
    defaultStepName: workflowTemplateStep.defaultStepName,
    defaultPhase: workflowTemplateStep.defaultPhase,
    defaultIsRepeatable: workflowTemplateStep.defaultIsRepeatable,
  });

  const onCancel = (): void => {
    setErrors([]);
    setStep({
      defaultStepName: workflowTemplateStep.defaultStepName,
      defaultPhase: workflowTemplateStep.defaultPhase,
      defaultIsRepeatable: workflowTemplateStep.defaultIsRepeatable,
    });
    closeModal();
  };

  const handleEditTemplateStep = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors([]);

    const res = await updateWorkflowTemplateStep({
      workflowTemplateStepUuid: workflowTemplateStep.uuid,
      defaultStepName: step.defaultStepName as string,
      defaultPhase: step.defaultPhase as string,
      defaultIsRepeatable:
        step.defaultPhase === ProjectPhase.ongoingMonitoring ? (step.defaultIsRepeatable as boolean) : false,
      rowVersion: workflowTemplateStep.rowVersion,
    });

    if (res.status === Status.Success) {
      Toast.success({ message: "Template step successfully updated" });
      shouldRefreshWorkflowTemplate();
      closeModal();
    }
    if (res.status === Status.Error && res.errors) {
      setErrors(res.errors);
    }
  }, setIsHandleEditTemplateStepLoading);

  return { step, setStep, isHandleEditTemplateStepLoading, handleEditTemplateStep, onCancel, errors };
};
